import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, dateShort, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill, Progress, Thumb, MetricCard, MoneyStat, SectionTitle, EmptyState, Field, ClientStatusBadge, Cover } from "@/components/Bits";
import ProductBoard from "@/components/ProductBoard";
import ProcurementTable from "@/components/ProcurementTable";
import FilesPanel from "@/components/FilesPanel";
import CoverImageField from "@/components/CoverImageField";
import ProjectNav from "@/components/ProjectNav";
import NotesPanel from "@/components/NotesPanel";
import BudgetView from "@/components/BudgetView";
import ScenariosPanel from "@/components/ScenariosPanel";
import OptionSetsPanel from "@/components/OptionSetsPanel";
import RoomBudgetBar from "@/components/RoomBudgetBar";
import ProjectDecisions from "@/components/ProjectDecisions";
import InternalNotes from "@/components/InternalNotes";
import ProjectInvoices from "@/components/ProjectInvoices";
import ClientPortalTab from "@/components/ClientPortalTab";
import ProjectMessages from "@/components/ProjectMessages";
import OriginalInquiryPanel from "@/components/OriginalInquiryPanel";
import { CalendarView } from "@/pages/Calendar";

const TABS = ["Overview", "Spaces", "Presentations", "Selections", "Messages", "Decisions", "Scenarios", "Procurement", "Budget", "Invoices", "Calendar", "Notes", "Files", "Activity", "Client Portal", "Settings"];

const TAB_SECTION = {
  Overview: "overview", Spaces: "selections", Presentations: "presentations",
  Selections: "selections", Invoices: "invoices", Calendar: "timeline",
  Files: "documents", Activity: "activity",
};
const STATUSES = ["Lead", "Active", "On Hold", "Completed", "Archived"];
const PHASES = ["Discovery", "Concept Development", "Design Development", "Client Review", "Sourcing", "Procurement", "Production", "Installation", "Styling", "Complete"];

const CLIENT_PHASE = {
  Discovery: "Getting Started",
  "Concept Development": "Concept Design",
  "Design Development": "Design Development",
  "Client Review": "Your Review",
  Sourcing: "Curating Selections",
  Procurement: "Ordering & Tracking",
  Production: "In Production",
  Installation: "Installation",
  Styling: "Final Styling",
  Complete: "Complete",
  Active: "In Progress",
  Lead: "Getting Started",
  "On Hold": "On Hold",
  Completed: "Complete",
  Archived: "Archived",
};
const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";

function AddSpaceForm({ projectId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", space_type: "", description: "", budget: 0, cover_image: "", cover_file_id: "", cover_focus: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/projects/${projectId}/spaces`, { ...form, budget: Number(form.budget) || 0 });
      toast.success("Space added");
      setForm({ name: "", space_type: "", description: "", budget: 0, cover_image: "", cover_file_id: "", cover_focus: "" });
      setOpen(false);
      onAdded(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  if (!open)
    return (
      <button data-testid="add-space-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
        <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Space
      </button>
    );
  return (
    <form onSubmit={submit} className="card-surface w-full space-y-5 p-6" data-testid="add-space-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Space Name">
          <input data-testid="space-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
        </Field>
        <Field label="Space Type">
          <input data-testid="space-type-input" value={form.space_type} onChange={(e) => setForm({ ...form, space_type: e.target.value })} placeholder="Living Room, Window Display…" className={input} />
        </Field>
        <Field label="Budget">
          <input data-testid="space-budget-input" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className={`num ${input}`} />
        </Field>
        <Field label="Description" className="sm:col-span-2">
          <textarea data-testid="space-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} />
        </Field>
        <div className="sm:col-span-2">
          <CoverImageField
            projectId={projectId}
            value={form}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          />
        </div>
      </div>
      <div className="flex gap-3">
        <button data-testid="space-submit-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save Space</button>
        <button type="button" onClick={() => setOpen(false)} className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
      </div>
    </form>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "Overview";  const [overview, setOverview] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [products, setProducts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [clientSummary, setClientSummary] = useState(null);
  const awaitingOnly = params.get("awaiting") === "1";

  const project = overview?.project;
  const visible = project?.client_sections || {};

  const loadAll = async () => {
    try {
      const o = await api.get(`/projects/${id}/overview`);
      setOverview(o.data);
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
      toast.error(apiError(e));
      return;
    }
    api.get(`/projects/${id}/spaces`).then((r) => setSpaces(r.data)).catch(() => setSpaces([]));
    api.get("/products", { params: { project_id: id } })
      .then((r) => setProducts(r.data)).catch(() => setProducts([]));
  };

  useEffect(() => {
    loadAll();
    api.get("/activities", { params: { project_id: id, limit: 60 } }).then((r) => setActivities(r.data)).catch(() => {});
    api.get("/presentations", { params: { project_id: id } }).then((r) => setPresentations(r.data)).catch(() => {});
    if (isStaff) api.get("/scenarios", { params: { project_id: id } }).then((r) => setScenarios(r.data)).catch(() => {});
    if (!isStaff) api.get(`/projects/${id}/client-summary`).then((r) => setClientSummary(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setTab = (t) => setParams({ tab: t });

  const patchProject = async (body) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, body);
      setOverview((o) => ({ ...o, project: { ...o.project, ...data } }));
      toast.success("Project updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const bySpace = useMemo(() => {
    const map = {};
    const list = awaitingOnly ? products.filter((p) => p.selection_status === "Awaiting Approval") : products;
    list.forEach((p) => {
      const key = p.space_id || "unassigned";
      (map[key] = map[key] || []).push(p);
    });
    return map;
  }, [products, awaitingOnly]);

  if (loadFailed && !project)
    return (
      <div className="py-24 text-center" data-testid="project-access-denied">
        <h2 className="font-serif text-2xl text-ink">This project isn’t available to you</h2>
        <p className="mt-3 text-sm text-stone">
          Ask your design team if you think you should have access.
        </p>
      </div>
    );
  if (!project) return <div className="py-24 text-center text-sm text-fog">Loading project…</div>;

  const f = overview.financials;
  const sc = overview.status_cards;

  return (
    <div className="space-y-10" data-testid="project-detail-page">
      <ProjectNav projectId={id} projectName={project.name} trail={[{ label: tab }]} hideSections />

      <div className="border-b border-line pb-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="overline mb-3">{project.project_type}</div>
            <h1 data-testid="project-title" className="font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">{project.name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-stone">
              {project.client_id ? (
                <Link to={`/clients/${project.client_id}`} className="hover:text-lime-dark">{project.client_name}</Link>
              ) : (
                <span>{project.client_name || "No client"}</span>
              )}
              <span className="text-line">·</span>
              {isStaff ? (
                <>
                  <select
                    data-testid="project-status-quick-select"
                    value={project.status}
                    onChange={(e) => patchProject({ status: e.target.value })}
                    className="rounded-sm border border-line bg-white px-3 py-1.5 text-xs outline-none focus:border-lime"
                  >
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <select
                    data-testid="project-phase-quick-select"
                    value={project.phase}
                    onChange={(e) => patchProject({ phase: e.target.value })}
                    className="rounded-sm border border-line bg-white px-3 py-1.5 text-xs outline-none focus:border-lime"
                  >
                    {PHASES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </>
              ) : (
                <>
                  <Pill tone="bg-lime text-ink">{CLIENT_PHASE[project.status] || project.status}</Pill>
                  <span className="text-line">·</span>
                  <span className="text-xs uppercase tracking-[0.15em] text-fog">Current Phase</span>
                  <span className="text-ink">{CLIENT_PHASE[project.phase] || project.phase}</span>
                </>
              )}
            </div>
          </div>
          {isStaff && (
            <div className="flex flex-wrap gap-3">
              <Link data-testid="project-add-product-btn" to={`/products/new?project=${id}`} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark">
                <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Product
              </Link>
              <Link data-testid="project-edit-btn" to={`/projects/${id}/edit`} className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:bg-line/50">
                <Pencil className="h-4 w-4" strokeWidth={1.5} /> Edit
              </Link>
            </div>
          )}
        </div>

        <div className="mt-8 flex gap-7 overflow-x-auto scroll-thin" data-testid="project-tabs">
          {TABS.filter((t) => isStaff || !["Budget", "Settings", "Procurement", "Decisions", "Scenarios", "Notes", "Client Portal"].includes(t))
            .filter((t) => isStaff || !TAB_SECTION[t] || visible[TAB_SECTION[t]] !== false)
            .map((t) => (            <button
              key={t}
              data-testid={`project-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap border-b-2 pb-3 text-sm transition-colors ${
                tab === t ? "border-lime font-medium text-ink" : "border-transparent text-stone hover:text-ink"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {!isStaff && TAB_SECTION[tab] && visible[TAB_SECTION[tab]] === false ? (
        <div className="card-surface p-10 text-center" data-testid="section-not-shared">
          <h2 className="font-serif text-2xl text-ink">This section isn’t shared with you</h2>
          <p className="mt-3 text-sm text-stone">Your design team keeps this part of the project private for now.</p>
        </div>
      ) : (
      <>
      {tab === "Overview" && (
        <div className="space-y-12" data-testid="tab-overview">
          <OriginalInquiryPanel snapshot={isStaff ? project.inquiry_snapshot : null} />

          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div className="card-surface overflow-hidden">
              {project.cover_file_id || project.cover_image ? (
                <Cover project={project} testId="project-cover-image" className="aspect-[16/9] w-full" />
              ) : (
                <Thumb className="aspect-[16/9] w-full" rounded="rounded-none" />
              )}
              <div className="p-7">
                <p className="text-sm leading-relaxed text-stone">{project.description || "No description yet."}</p>
                <div className="mt-7">
                  <div className="mb-2 flex items-center justify-between text-xs">
                    <span className="overline">Progress</span>
                    <span className="num text-stone">{project.progress || 0}%</span>
                  </div>
                  <Progress value={project.progress || 0} />
                </div>
                <div className="mt-7 grid grid-cols-2 gap-6 border-t border-line pt-6 sm:grid-cols-4">
                  {[["Start", project.start_date], ["Target", project.target_completion_date], ["Install", project.install_date]]
                    .filter(([, v]) => isStaff || (visible.timeline !== false && v))
                    .map(([l, v]) => (
                    <div key={l}>
                      <div className="overline">{l}</div>
                      <div className="num mt-1 text-sm text-ink">{dateShort(v)}</div>
                    </div>
                  ))}
                  <div>
                    <div className="overline">Spaces</div>
                    <div className="num mt-1 text-sm text-ink">{overview.space_count}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              {isStaff && (
                <div className="card-surface p-7">
                  <SectionTitle>Financial Summary</SectionTitle>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <MoneyStat testId="overview-budget" label="Overall Budget" value={f.budget_total} />
                    <MoneyStat testId="overview-approved" label="Approved Selections" value={f.approved} tone="text-lime-dark" />
                    <MoneyStat testId="overview-purchased" label="Purchased" value={f.purchased} />
                    <MoneyStat testId="overview-remaining" label="Remaining" value={f.remaining} tone={f.remaining < 0 ? "text-clay" : "text-ink"} />
                  </div>
                </div>
              )}
              {(isStaff || visible.selections !== false) && (
              <div className="card-surface p-7">
                <SectionTitle>{isStaff ? "Pipeline" : "Your Selections"}</SectionTitle>
                {isStaff ? (
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
                  {[["Awaiting Approval", sc.awaiting_approval], ["Approved", sc.approved], ["To Order", sc.to_order], ["In Transit", sc.in_transit], ["Delivered", sc.delivered]].map(([l, v]) => (
                    <div key={l} data-testid={`overview-card-${l.toLowerCase().replace(/\s/g, "-")}`}>
                      <div className="num text-2xl text-ink">{v}</div>
                      <div className="overline mt-1 leading-tight">{l}</div>
                    </div>
                  ))}
                </div>
                ) : (
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-3" data-testid="client-selection-counts">
                  {[
                    ["Total Selections", clientSummary?.total ?? 0, null],
                    ["Approved", clientSummary?.approved ?? 0, null],
                    ["Need Your Approval", clientSummary?.awaiting_approval ?? 0, `/projects/${id}?tab=Selections&awaiting=1`],
                    ["On the Way", clientSummary?.on_the_way ?? 0, null],
                    ["Delivered", clientSummary?.delivered ?? 0, null],
                    ["Installed", clientSummary?.installed ?? 0, null],
                  ].map(([l, v, link]) =>
                    link ? (
                      <Link key={l} to={link} data-testid="client-need-approval-link" className="rounded-sm transition-colors hover:bg-bone">
                        <div className="num text-2xl text-lime-dark">{v}</div>
                        <div className="overline mt-1 leading-tight">{l}</div>
                      </Link>
                    ) : (
                      <div key={l}>
                        <div className="num text-2xl text-ink">{v}</div>
                        <div className="overline mt-1 leading-tight">{l}</div>
                      </div>
                    )
                  )}
                </div>
                )}
                {project.install_date && (isStaff || visible.timeline !== false) && (
                  <div className="mt-7 border-t border-line pt-5 text-sm text-stone">
                    Next milestone — <span className="text-ink">Installation {dateShort(project.install_date)}</span>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {(isStaff || visible.activity !== false) && (
          <section>
            <SectionTitle>Recent Project Activity</SectionTitle>
            <div className="card-surface divide-y divide-line">
              {activities.slice(0, 8).map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 px-6 py-4 text-sm">
                  {isStaff ? (
                    <span className="text-ink"><span className="font-medium">{a.user_name}</span> {a.action}{a.entity_name && a.action.indexOf(a.entity_name) === -1 ? ` — ${a.entity_name}` : ""}</span>
                  ) : (
                    <span className="text-ink">{a.client_action || a.detail || a.entity_name}</span>
                  )}
                  <span className="num shrink-0 text-xs text-fog">{timeAgo(a.created_at)}</span>
                </div>
              ))}
              {!activities.length && <div className="px-6 py-10 text-center text-sm text-fog">No activity yet.</div>}
            </div>
          </section>
          )}
        </div>
      )}

      {tab === "Spaces" && (
        <div className="space-y-8" data-testid="tab-spaces">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <SectionTitle>Spaces &amp; Rooms</SectionTitle>
            {isStaff && <AddSpaceForm projectId={id} onAdded={loadAll} />}
          </div>
          {spaces.length === 0 ? (
            <EmptyState testId="spaces-empty" title="No spaces yet" body="Add a room or set to organise selections." />
          ) : (
            <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
              {spaces.map((s, i) => (
                <Link
                  key={s.id}
                  to={`/spaces/${s.id}`}
                  data-testid={`space-card-${s.id}`}
                  className="card-surface card-hover group animate-rise overflow-hidden"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-line">
                    {s.cover_file_id || s.cover_image ? (
                      <Cover project={s} className="h-full w-full" imgClassName="transition-transform duration-500 group-hover:scale-[1.03]" />
                    ) : (
                      <Thumb className="h-full w-full" rounded="rounded-none" />
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl text-ink">{s.name}</h3>
                    <div className="mt-1 text-sm text-stone">{s.space_type || "Space"} · {s.product_count} selections</div>
                    {s.show_financials && (
                      <div className="mt-5 border-t border-line pt-5">
                        <RoomBudgetBar
                          testId={`room-budget-bar-${s.id}`}
                          budget={s.budget}
                          approved={s.approved_total}
                          pending={s.pending_total}
                        />
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Presentations" && (
        <div className="space-y-8" data-testid="tab-presentations">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <SectionTitle>Design Presentations</SectionTitle>
            {isStaff && (
              <Link data-testid="create-presentation-btn" to={`/presentations/new?project=${id}`} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
                <Plus className="h-4 w-4" strokeWidth={1.5} /> Create Presentation
              </Link>
            )}
          </div>
          {presentations.length === 0 ? (
            <EmptyState testId="presentations-empty" title="No presentations yet" body="Group coordinated selections into a design presentation and publish it to your client." />
          ) : (
            <div className="grid gap-7 md:grid-cols-2 xl:grid-cols-3">
              {presentations.map((p) => (
                <Link key={p.id} to={`/presentations/${p.id}`} data-testid={`presentation-card-${p.id}`} className="card-surface card-hover overflow-hidden">
                  <div className="aspect-[16/10] bg-line">
                    {p.hero_image ? <img src={p.hero_image} alt={p.name} className="h-full w-full object-cover" /> : <Thumb className="h-full w-full" rounded="rounded-none" />}
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-serif text-lg text-ink">{p.title || p.name}</h3>
                      <Pill tone={p.status === "Published" ? "bg-lime text-ink" : p.status === "Approved" ? "bg-[#DDE3C8] text-ink" : "bg-line text-stone"}>{p.status}</Pill>
                    </div>
                    <div className="mt-2 text-sm text-stone">{p.space_name || "Whole project"}</div>
                    <div className="num mt-4 text-xs text-fog">
                      {p.selection_count} selections{p.awaiting_count ? ` · ${p.awaiting_count} awaiting approval` : ""}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Selections" && (
        <div className="space-y-10" data-testid="tab-selections">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <SectionTitle>{isStaff ? "Selections" : "Your Selections"}</SectionTitle>
            <div className="flex flex-wrap items-center gap-3">
              {isStaff && (
                <Link
                  to={`/projects/${id}/invoice/new`}
                  data-testid="create-invoice-from-selections"
                  className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-3 py-2 text-xs text-ink transition-colors hover:border-lime"
                >
                  Create Invoice from Approved Selections
                </Link>
              )}
              <button
                data-testid="selections-awaiting-filter"
                onClick={() => setParams(awaitingOnly ? { tab: "Selections" } : { tab: "Selections", awaiting: "1" })}
                className={`rounded-sm border px-3 py-2 text-xs transition-colors ${
                  awaitingOnly ? "border-lime bg-lime text-ink" : "border-line bg-white text-stone hover:border-fog"
                }`}
              >
                {isStaff ? "Awaiting approval only" : "Need your approval only"}
              </button>
              {isStaff && (
                <Link to={`/products/new?project=${id}`} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
                  <Plus className="h-4 w-4" strokeWidth={1.5} /> Add Product
                </Link>
              )}
            </div>
          </div>
          {spaces.map((s) => (
            <div key={s.id}>
              <div className="mb-5 flex items-center justify-between border-b border-line pb-3">
                <Link to={`/spaces/${s.id}`} className="font-serif text-xl text-ink hover:text-lime-dark">{s.name}</Link>
                <span className="num text-xs text-fog">{(bySpace[s.id] || []).length} items</span>
              </div>
              <ProductBoard products={bySpace[s.id] || []} addLink={isStaff ? `/products/new?project=${id}&space=${s.id}` : null} emptyTitle={isStaff ? `No selections in ${s.name}` : `${s.name} selections are coming soon`} compact />
            </div>
          ))}
          {bySpace.unassigned && (
            <div>
              <div className="mb-5 border-b border-line pb-3 font-serif text-xl text-ink">Unassigned</div>
              <ProductBoard products={bySpace.unassigned} compact />
            </div>
          )}
        </div>
      )}

      {tab === "Messages" && <ProjectMessages projectId={id} />}

      {tab === "Calendar" && <CalendarView projectId={id} />}

      {tab === "Notes" && isStaff && (
        <InternalNotes projectId={id} spaces={spaces} selections={products} vendors={[]} />
      )}

      {tab === "Decisions" && isStaff && <ProjectDecisions projectId={id} />}

      {tab === "Scenarios" && isStaff && (
        <div className="space-y-16">
          <ScenariosPanel projectId={id} spaces={spaces} />
          <OptionSetsPanel projectId={id} spaces={spaces} scenarios={scenarios} />
        </div>
      )}

      {tab === "Procurement" && isStaff && (
        <div data-testid="tab-procurement">
          <SectionTitle>Procurement</SectionTitle>
          <ProcurementTable projectId={id} embedded />
        </div>
      )}

      {tab === "Budget" && isStaff && <BudgetView projectId={id} />}

      {tab === "Invoices" && <ProjectInvoices projectId={id} />}

      {tab === "Files" && (
        <div data-testid="tab-files">
          <SectionTitle>Project Files</SectionTitle>
          <FilesPanel projectId={id} coverFileId={project.cover_file_id} onCoverChange={(p) => setProject(p)} />
        </div>
      )}

      {tab === "Activity" && (
        <div data-testid="tab-activity">
          <SectionTitle>{isStaff ? "Activity Log" : "Project Updates"}</SectionTitle>
          <div className="card-surface divide-y divide-line">
            {activities.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 text-sm">
                <div>
                  {isStaff ? (
                    <>
                      <span className="text-ink"><span className="font-medium">{a.user_name}</span> {a.action}</span>
                      {a.entity_name && <span className="text-stone"> — {a.entity_name}</span>}
                      {a.detail && <div className="mt-1 text-xs text-fog">{a.detail}</div>}
                    </>
                  ) : (
                    <span className="text-ink">{a.client_action || a.detail || a.entity_name}</span>
                  )}
                </div>
                <span className="num text-xs text-fog">{dateShort(a.created_at)} · {timeAgo(a.created_at)}</span>
              </div>
            ))}
            {!activities.length && <div className="px-6 py-10 text-center text-sm text-fog">No activity yet.</div>}
          </div>
        </div>
      )}

      {tab === "Client Portal" && isStaff && (
        <ClientPortalTab
          projectId={id}
          onPortalToggle={(enabled) =>
            setOverview((o) => ({ ...o, project: { ...o.project, client_portal_enabled: enabled } }))
          }
        />
      )}

      {tab === "Settings" && isStaff && (
        <div className="space-y-10" data-testid="tab-settings">
          <div className="card-surface p-7">
            <SectionTitle>Project Settings</SectionTitle>
            <label className="flex items-center gap-3 text-sm text-ink">
              <input
                data-testid="project-portal-setting"
                type="checkbox"
                checked={!!project.client_portal_enabled}
                onChange={(e) => patchProject({ client_portal_enabled: e.target.checked })}
                className="h-4 w-4 accent-[#BEC221]"
              />
              Client portal access enabled
            </label>
            <label className="mt-4 flex items-center gap-3 text-sm text-ink">
              <input
                data-testid="project-budget-impact-setting"
                type="checkbox"
                checked={!!project.show_budget_impact}
                onChange={(e) => patchProject({ show_budget_impact: e.target.checked })}
                className="h-4 w-4 accent-[#BEC221]"
              />
              Show Budget Impact During Approvals
            </label>
            <label className="mt-4 flex items-center gap-3 text-sm text-ink">
              <input
                data-testid="project-selection-board-setting"
                type="checkbox"
                checked={project.client_selection_board_visible !== false}
                onChange={(e) => patchProject({ client_selection_board_visible: e.target.checked })}
                className="h-4 w-4 accent-[#BEC221]"
              />
              Client Selection Board visible to client
            </label>
            <p className="mt-2 max-w-xl text-xs leading-relaxed text-fog">
              Turn this off and the client no longer sees the selection board, product prices or
              selection counts — while your team keeps full access to pricing, approvals, procurement
              and tracking. Selections you share inside a published presentation still reach the
              client.
            </p>
            <p className="mt-2 max-w-xl text-xs leading-relaxed text-fog">
              When enabled, the client sees how approving a selection affects the relevant room or
              project budget. Off by default — turn it on intentionally. A room can override this in
              its own settings.
            </p>
            <p className="mt-3 max-w-xl text-xs leading-relaxed text-fog">
              When enabled, client users linked to {project.client_name || "this client"} can view this project. Cost,
              markup, margin and internal notes are never exposed to client accounts.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={`/projects/${id}/edit`} className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">Edit project details</Link>
              {isAdmin && (
                <button
                  data-testid="project-delete-btn"
                  onClick={async () => {
                    if (!window.confirm("Delete this project and all its selections?")) return;
                    await api.delete(`/projects/${id}`);
                    toast.success("Project deleted");
                    navigate("/projects");
                  }}
                  className="rounded-sm border border-clay bg-white px-4 py-2.5 text-sm text-clay transition-colors hover:bg-[#F6DED6]"
                >
                  Delete project
                </button>
              )}
            </div>
          </div>
          <div className="card-surface p-7">
            <NotesPanel entityType="project" entityId={id} projectId={id} title="Project Internal Notes" />
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
